import { useEffect, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore, applyTheme } from '@/stores/uiStore'
import { setAuthFailureHandler } from '@/lib/api/client'
import '@/i18n'

/**
 * Query defaults.
 *
 * `retry` deliberately skips 4xx: a 403 or 404 will not fix itself, and retrying three
 * times just delays the error the user needs to see. Only 5xx and network faults retry.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status && status >= 400 && status < 500) return false
        return failureCount < 2
      },
    },
    mutations: { retry: false },
  },
})

export function Providers({ children }: { children: ReactNode }) {
  const bootstrap = useAuthStore((state) => state.bootstrap)
  const clear = useAuthStore((state) => state.clear)
  const theme = useUiStore((state) => state.theme)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  useEffect(() => {
    // When a refresh finally fails, drop local state and clear cached server data so the
    // next user of this browser cannot see the previous session's rows.
    setAuthFailureHandler(() => {
      clear()
      queryClient.clear()
    })
    void bootstrap()
  }, [bootstrap, clear])

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}

export { queryClient }
